import React from "react";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { getStatusChip } from "../../utils/utils";
import {
  formatPortalDateTimeFull,
  formatPortalAssigneeHint,
  getPortalAssigneeChipLabel,
  getPortalAssigneeChipStyles,
  isGenericPortalTypeName,
  ticketHeaderSubject,
} from "./clientPortalTicketListUtils";

function buildTicketMetaTooltip(ticket) {
  const parts = [];
  const openedAt = formatPortalDateTimeFull(ticket?.createdAt);
  if (openedAt) parts.push(`Aberta em ${openedAt}`);
  if (ticket?.channelName) parts.push(`Canal: ${ticket.channelName}`);
  if (ticket?.typeName && !isGenericPortalTypeName(ticket.typeName)) {
    parts.push(`Tipo: ${ticket.typeName}`);
  }
  if (!ticket?.isOwnTicket && ticket?.clientName) {
    parts.push(`Cliente: ${ticket.clientName}`);
  }
  return parts.join("\n");
}

function PortalAssigneeChip({ ticket }) {
  const theme = useTheme();
  const label = getPortalAssigneeChipLabel(ticket);
  const hint = formatPortalAssigneeHint(ticket);
  const { bg, color } = getPortalAssigneeChipStyles(ticket, theme);
  const kind = ticket?.assignedToKind;
  const icon =
    kind === "chatbot" ? (
      <SmartToyOutlinedIcon sx={{ fontSize: "14px !important" }} />
    ) : kind === "human" || kind === "agent" ? (
      <SupportAgentOutlinedIcon sx={{ fontSize: "14px !important" }} />
    ) : undefined;

  return (
    <Tooltip title={hint}>
      <Chip
        icon={icon}
        label={label}
        size="small"
        sx={{
          backgroundColor: bg,
          color,
          fontSize: "0.75rem",
          height: 24,
          maxWidth: 200,
          "& .MuiChip-label": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
          "& .MuiChip-icon": { color: "inherit" },
        }}
      />
    </Tooltip>
  );
}

export default function ClientPortalTicketHeader({ ticket, isClosed, onBack }) {
  const theme = useTheme();
  const subject = ticketHeaderSubject(ticket);
  const metaTooltip = buildTicketMetaTooltip(ticket);
  const protocolId = ticket?.id;

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        minWidth: 0,
      }}
    >
      <Tooltip title="Voltar às solicitações">
        <IconButton onClick={onBack} aria-label="Voltar às solicitações" sx={{ flexShrink: 0 }}>
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>

      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <Typography
          variant="subtitle1"
          noWrap
          title={subject}
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {subject}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          spacing={0.75}
          sx={{ mt: 0.5, minWidth: 0 }}
        >
          {protocolId ? (
            <Tooltip
              title={
                metaTooltip ? (
                  <span style={{ whiteSpace: "pre-line" }}>{metaTooltip}</span>
                ) : (
                  `Protocolo: ${protocolId}`
                )
              }
            >
              <Chip
                label={`Protocolo: ${protocolId}`}
                size="small"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  height: 24,
                  maxWidth: 140,
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </Tooltip>
          ) : null}

          <PortalAssigneeChip ticket={ticket} />

          {ticket?.status != null ? getStatusChip(ticket.status, theme) : null}

          {isClosed ? (
            <Chip
              size="small"
              variant="outlined"
              label="Encerrada"
              sx={{ height: 24, fontSize: "0.72rem" }}
            />
          ) : null}

          {!ticket?.isOwnTicket && ticket?.clientName ? (
            <Chip
              size="small"
              variant="outlined"
              label={ticket.clientName}
              sx={{
                height: 24,
                fontSize: "0.72rem",
                maxWidth: 160,
                "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />
          ) : null}

          {ticket?.channelName ? (
            <Chip
              size="small"
              variant="outlined"
              label={ticket.channelName}
              sx={{ height: 24, fontSize: "0.72rem", maxWidth: 140 }}
            />
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}
